import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { Button } from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { staffAction } from '../../data/dataProvider';
import { ActionDialog } from '../../components/ActionDialog';
import { useCanModerate } from '../../auth/useCanModerate';

/**
 * Скрыть (active → cancelled) / восстановить (cancelled → active) приглашение.
 * Симметричная пара с подтверждением и причиной (audit).
 */
export function HideInvitationButton(): JSX.Element | null {
  const record = useRecordContext<{ id: string; status: string }>();
  const notify = useNotify();
  const refresh = useRefresh();
  const canModerate = useCanModerate();
  const [open, setOpen] = useState(false);

  if (!record || !canModerate) return null;
  const isHidden = record.status === 'cancelled';
  if (record.status !== 'active' && !isHidden) return null;

  async function confirm(reason: string): Promise<void> {
    if (!record) return;
    try {
      if (isHidden) {
        await staffAction('POST', `/staff/invitations/${record.id}/unhide`, {
          reason,
        });
        notify('Приглашение восстановлено', { type: 'success' });
      } else {
        await staffAction('POST', `/staff/invitations/${record.id}/hide`, {
          reason,
        });
        notify('Приглашение скрыто', { type: 'success' });
      }
      setOpen(false);
      refresh();
    } catch (e) {
      notify(`Ошибка: ${(e as Error).message}`, { type: 'error' });
    }
  }

  return (
    <>
      <Button
        size="small"
        color={isHidden ? 'success' : 'warning'}
        startIcon={isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {isHidden ? 'Восстановить' : 'Скрыть'}
      </Button>
      <ActionDialog
        open={open}
        title={isHidden ? 'Восстановить приглашение?' : 'Скрыть приглашение?'}
        description={
          isHidden
            ? 'Приглашение вернётся в статус «Активно» и снова появится в ленте.'
            : 'Приглашение будет отменено и исчезнет из ленты. Действие обратимо — доступно восстановление.'
        }
        confirmLabel={isHidden ? 'Восстановить' : 'Скрыть'}
        confirmColor={isHidden ? 'success' : 'warning'}
        reason={{
          label: isHidden ? 'Причина восстановления' : 'Причина скрытия',
          required: true,
          minLength: 3,
        }}
        onConfirm={confirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
